<script setup lang="ts">
import { PropType } from 'vue'
import { useNuxtApp } from '#app'
import Substitutions from '~/models/Product/Substitutions'
import ImagePreview from '~/components/elements/ImagePreview.vue'
import { addProductToBasket } from '~/utils/addProductToBasket'
import { useBasketStore } from '~/stores/basket'
import { GtmItemListNameEnum } from '~/models/DataLayer/GtmItemListNameEnum'
import { GtmEcommerceEventTypeEnum } from '~/models/DataLayer/GtmEcommerceEventTypeEnum'
import GtmEcommerceEvent from '~/models/DataLayer/GtmEcommerceEvent'
import DataLayer from '~/extensions/DataLayer'
import Product from '~/models/Product/Product'
import { transformProductToGtmProduct } from '~/utils/transformProductToGtmProduct'
import SubstitutionProduct from '~/models/Product/SubstitutionProduct'

const props = defineProps({
    currentProduct: {
        type: Object as PropType<Product>,
        required: true,
    },
    substitutions: {
        type: Object as PropType<Substitutions>,
        required: true,
    },
})

const product = computed(() => {
    return props.substitutions.products[0]
})

const addToBasket = (product: Product | SubstitutionProduct, isSubstitution: boolean) => {
    const basket = useBasketStore()
    const amount = basket.productAmounts[product.id] ?? 0
    let itemListName =
        props.substitutions.type === 'manual'
            ? GtmItemListNameEnum.ProductDetailSubstitutionManualAggressive
            : GtmItemListNameEnum.ProductDetailSubstitutionAutoAggressive
    if (!isSubstitution) {
        itemListName =
            props.substitutions.type === 'manual'
                ? GtmItemListNameEnum.ProductDetailSubstitutionManualAggressiveDecline
                : GtmItemListNameEnum.ProductDetailSubstitutionAutoAggressiveDecline
    }

    addProductToBasket({
        productId: product.id,
        amount: amount + 1,
        showPreBasket: true,
        itemListName,
    })
    if (amount > 0) {
        hideModal()
    }
}

const hideModal = () => {
    useNuxtApp().$bus.$emit('hideModal')
}

onMounted(() => {
    useNuxtApp().$bus.$on('showProductSubstitutionsModal', () => {
        useNuxtApp().$bus.$emit('showModal', 'modal-product-substitutions')

        const ecommerceEvent = {
            type:
                props.substitutions.type === 'manual'
                    ? GtmEcommerceEventTypeEnum.ViewProductSubstitutionManualAggressive
                    : GtmEcommerceEventTypeEnum.ViewProductSubstitutionAutoAggressive,
            products: [transformProductToGtmProduct(props.currentProduct)],
        } as GtmEcommerceEvent

        DataLayer.pushEcommerceEventToDataLayer(ecommerceEvent)
    })
})
</script>

<template>
    <Modal
        modal-name="modal-product-substitutions"
        size="lg"
        :title="$t('productSubstitutions.title')"
        @close="addToBasket(props.currentProduct, false)"
    >
        <div class="substitution--badge">
            {{ $t('productSubstitutions.badge') }}
        </div>
        <div class="substitution--product">
            <div class="product__image" :data-id="product.id">
                <NuxtLink :to="product.url" class="img-container" @click="hideModal">
                    <ImagePreview :image-url="product.img" :image-alt="product.title" :lazy-image="true" />

                    <div class="product-discount">
                        <div v-if="product.sale" class="discount">-{{ product.sale }} %</div>
                    </div>
                </NuxtLink>
            </div>
            <div class="product__content">
                <nuxt-link :to="product.url" @click="hideModal">
                    <h3 class="product__name" v-html="product.title"></h3>
                </nuxt-link>

                <div class="product__desc" v-html="product.info" />

                <nuxt-link :to="product.url" class="product__price" @click="hideModal()">
                    <s v-if="product.oldPrice" class="old me-1 notranslate" v-html="$formatCurrency(product.oldPrice)"></s>
                    <b v-if="product.price" class="notranslate" v-html="$formatCurrency(product.price)"></b>
                </nuxt-link>
            </div>
        </div>
        <div class="substitution--buttons">
            <div class="button cancel" type="submit" @click="addToBasket(currentProduct, false)">
                {{ $t('productSubstitutions.keepCurrentProduct') }}
            </div>
            <UiTheButton class="button" type="submit" @click="addToBasket(product, true)">
                {{ $t('productSubstitutions.chooseBetterProduct') }}
            </UiTheButton>
        </div>
    </Modal>
</template>

<style lang="scss" scoped>
.substitution {
    &--badge {
        text-align: center;
        font-weight: 700;
        font-size: 16px;
        line-height: 19px;
        color: $color-cta;
        background-color: $color-cta-extra-light;
        padding: 10px;
        border-radius: 10px;
    }

    &--product {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin: 20px 0 40px;

        @media only screen and (max-width: 767px) {
            flex-direction: column;
        }

        .product__content {
            flex: 0 0 50%;

            @media only screen and (max-width: 767px) {
                text-align: center;
            }

            .product__name {
                font-weight: 590;
                font-size: 16px;
                line-height: 19px;
                margin: 5px 0;

                @media only screen and (min-width: 768px) {
                    font-size: 20px;
                    line-height: 24px;
                    margin: 10px 0;
                }
            }

            .product__desc {
                font-weight: 400;
                font-size: 14px;
                line-height: 17px;
            }

            .product__price {
                font-weight: 700;
                font-size: 18px;
                line-height: 21px;

                .old {
                    font-weight: 400;
                    color: $color-dark;
                    font-size: 14px;
                    margin-right: 0.5em;
                    line-height: 17px;

                    @media only screen and (min-width: 768px) {
                        font-size: 12px;
                    }
                }
            }
        }

        .product__image {
            flex: 0 0 50%;

            .img-container {
                height: auto;
                min-height: 100px;
                min-width: 100px;
                padding: 0;
            }
        }

        .product-discount {
            position: absolute;
            right: 7px;
            top: 0;
            font-size: 13px;
            color: $color-dark;
            display: flex;
            flex-direction: column;
            align-items: flex-end;

            .discount {
                margin-bottom: 0.5rem;
            }
        }
    }

    &--buttons {
        display: flex;
        justify-content: space-between;
        align-items: center;
        text-align: center;

        @media only screen and (max-width: 767px) {
            flex-direction: column-reverse;
        }

        .button {
            flex: 0 0 50%;

            &.cancel {
                color: $color-cta;
                text-decoration: underline;
                cursor: pointer;
                &:hover {
                    color: $color-cta-dark;
                    text-decoration: none;
                }
            }
        }

        @media only screen and (max-width: 767px) {
            .cancel {
                margin-top: 10px;
            }
        }
    }
}
</style>
